<div class="flex flex-col gap-8">
  <div class="flex flex-col gap-8">
    <!-- Selling Price Section - Premium/Prominent UI -->
    <div class="rounded-2xl border border-primary-2 bg-primary-1 p-8 shadow-sm">
      <div class="flex flex-col items-center gap-4 text-center">
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-primary-2 text-primary-9">
          <mat-icon fontIcon="payments" class="text-3xl"></mat-icon>
        </div>
        <div class="flex flex-col gap-1">
          <h2 class="text-2xl font-bold text-neutral-12">Prix de vente net vendeur souhaité</h2>
          <p class="text-neutral-11">C'est l'information la plus déterminante pour votre annonce.</p>
        </div>

        <div class="mt-4 w-full max-w-md">
          <mat-form-field
            appearance="outline"
            class="w-full [&_.mat-mdc-text-field-wrapper]:pt-3! [&_.mat-mdc-text-field-wrapper]:pb-3!"
          >
            <input
              matInput
              type="text"
              inputmode="numeric"
              [ngModel]="formattedSellingPrice"
              (ngModelChange)="onSellingPriceInput($event)"
              placeholder="0"
              aria-label="Prix de vente net vendeur souhaité"
              class="text-center text-5xl font-bold tracking-[0.05em]"
            />
            <span matSuffix class="mr-4 text-2xl font-bold text-primary-9">€</span>
          </mat-form-field>
          @if (pricePerSquareMeter(); as priceM2) {
            <div
              class="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary-2 px-3.5 py-1.5 text-sm font-semibold text-primary-9 border border-primary-4 animate-in fade-in duration-200"
            >
              <mat-icon class="text-xs! w-3.5! h-3.5!">square_foot</mat-icon>
              <span
                >Soit env. {{ priceM2 | number: '1.0-0' : 'fr-FR' }} €/m² (pour {{ habitableSurface() }} m²
                hab.)</span
              >
            </div>
          }

          <!-- DVF Reference & Estimation Widget -->
          <div class="mt-4">
            <etxe-property-dvf-market-reference
              [propertyId]="propertyId()"
              [propertyLocation]="propertyLocation()"
              [propertyType]="propertyType()"
              [habitableSurface]="habitableSurface()"
              [propertyDvf]="propertyDvf()"
              [currentPrice]="sellingPriceEuros()"
              [showGauge]="false"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Charges & Taxes Section -->
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-3">
        <div class="h-8 w-1 rounded-full bg-primary-9"></div>
        <h3 class="text-lg font-bold text-neutral-12">Frais et Taxes</h3>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <!-- Monthly Charges -->
        <div class="flex flex-col gap-2 rounded-xl border border-neutral-3 bg-neutral-1 p-4">
          <div class="mb-1 flex items-center gap-2">
            <mat-icon fontIcon="receipt_long" class="text-neutral-8"></mat-icon>
            <span class="text-sm font-semibold tracking-wider text-neutral-11 uppercase"
              >Charges courantes</span
            >
          </div>
          <mat-form-field
            appearance="outline"
            class="w-full [&_.mat-mdc-form-field-infix]:pt-3! [&_.mat-mdc-form-field-infix]:pb-3!"
          >
            <input
              matInput
              class="tracking-tight"
              type="number"
              min="0"
              [ngModel]="getEuro(model().chargesCents)"
              (ngModelChange)="updateCents('chargesCents', $event)"
              placeholder="0"
              aria-label="Charges courantes"
            />
            <span matSuffix class="mr-2 text-neutral-11">€/mois</span>
          </mat-form-field>
        </div>

        <!-- Estimated Notary Fees (Informational display card) -->
        <div class="flex flex-col justify-between rounded-xl border border-neutral-3 bg-neutral-1 p-4">
          <div>
            <div class="mb-3 flex items-center justify-between">
              <div class="flex items-center gap-2">
                <mat-icon fontIcon="gavel" class="text-neutral-8 text-[20px] w-5 h-5"></mat-icon>
                <span class="text-xs font-bold tracking-wider text-neutral-11 uppercase"
                  >Frais de notaire</span
                >
              </div>
              <span
                class="rounded-full px-2.5 py-0.5 text-[11px] font-medium"
                [class]="
                  isNewBuild()
                    ? 'bg-emerald-2 text-emerald-11 border border-emerald-4'
                    : 'bg-neutral-3 text-neutral-11 border border-neutral-4'
                "
              >
                {{ isNewBuild() ? 'Bien neuf' : 'Bien ancien' }}
              </span>
            </div>

            <!-- Price value & percentage -->
            <div class="flex items-baseline gap-2.5 my-1">
              @if (model().taxesAndFees.notaryFeesCents; as notaryCents) {
                <span class="text-4xl font-bold tracking-tight text-neutral-12">
                  {{ notaryCents / 100 | number: '1.0-0' : 'fr-FR' }}
                  <span class="text-xl font-semibold text-neutral-10">€</span>
                </span>
                @if (notaryFeesPercentage(); as pct) {
                  <span
                    class="inline-flex items-center rounded-md bg-primary-2 px-2 py-0.5 text-sm font-bold text-primary-9 border border-primary-4"
                  >
                    env. {{ pct | number: '1.1-1' }} %
                  </span>
                }
              } @else {
                <span class="text-xl font-medium text-neutral-8 italic"> Indiquez un prix de vente </span>
              }
            </div>
            <p class="text-[11px] text-neutral-10 leading-tight">💡 Estimation automatique indicative.</p>
          </div>
        </div>
      </div>

      <!-- Taxes Section (Nested grouping) -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <!-- Property Tax -->
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-neutral-11">Taxe foncière</span>
          <mat-form-field
            appearance="outline"
            class="w-full [&_.mat-mdc-form-field-infix]:pt-3! [&_.mat-mdc-form-field-infix]:pb-3!"
          >
            <input
              matInput
              class="tracking-tight"
              type="number"
              min="0"
              [ngModel]="getEuro(model().taxesAndFees.propertyTaxCents)"
              (ngModelChange)="updateTaxCents('propertyTaxCents', $event)"
              placeholder="0"
              aria-label="Taxe foncière"
            />
            <span matSuffix class="mr-2 text-xs text-neutral">€/an</span>
          </mat-form-field>
        </div>

        <!-- Housing Tax (Only for Secondary Residence) -->
        @if (isSecondaryResidence()) {
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-neutral-11">Taxe d'habitation (Résidence secondaire)</span>
            <mat-form-field
              appearance="outline"
              class="w-full [&_.mat-mdc-form-field-infix]:pt-3! [&_.mat-mdc-form-field-infix]:pb-3!"
            >
              <input
                matInput
                class="tracking-tight"
                type="number"
                min="0"
                [ngModel]="getEuro(model().taxesAndFees.housingTaxCents)"
                (ngModelChange)="updateTaxCents('housingTaxCents', $event)"
                placeholder="0"
                aria-label="Taxe d'habitation"
              />
              <span matSuffix class="mr-2 text-xs text-neutral">€/an</span>
            </mat-form-field>
          </div>
        }

        <!-- TEOM -->
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-neutral-11">Taxe ordures ménagères (TEOM)</span>
          <mat-form-field
            appearance="outline"
            class="w-full [&_.mat-mdc-form-field-infix]:pt-3! [&_.mat-mdc-form-field-infix]:pb-3!"
          >
            <input
              matInput
              class="tracking-tight"
              type="number"
              min="0"
              [ngModel]="getEuro(model().taxesAndFees.wasteCollectionTaxCents)"
              (ngModelChange)="updateTaxCents('wasteCollectionTaxCents', $event)"
              placeholder="0"
              aria-label="Taxe ordures ménagères (TEOM)"
            />
            <span matSuffix class="mr-2 text-xs text-neutral">€/an</span>
          </mat-form-field>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <!-- Co-ownership Charges -->
        @if (propertyIsCoOwnership()) {
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-neutral-11">Charges de copropriété (approx.)</span>
            <mat-form-field
              appearance="outline"
              class="w-full [&_.mat-mdc-form-field-infix]:pt-3! [&_.mat-mdc-form-field-infix]:pb-3!"
            >
              <input
                matInput
                class="tracking-tight"
                type="number"
                min="0"
                [ngModel]="getEuro(model().coOwnershipChargesCents)"
                (ngModelChange)="updateCents('coOwnershipChargesCents', $event)"
                placeholder="0"
                aria-label="Charges de copropriété"
              />
              <span matSuffix class="mr-2 text-xs text-neutral">€/mois</span>
            </mat-form-field>
          </div>
        }
      </div>
    </div>

    <!-- Precisions Section -->
    <div class="flex flex-col gap-4 border-t border-neutral-3 pt-6">
      <div class="flex items-start gap-2.5">
        <mat-icon fontIcon="lock" class="text-primary text-[20px] w-5 h-5 mt-0.5"></mat-icon>
        <div>
          <h3 class="text-md font-semibold text-neutral-12">Précisions ou commentaires</h3>
          <p class="text-xs text-neutral-10 mt-0.5">
            Ces précisions sont
            <strong>réservées exclusivement aux agents immobiliers partenaires</strong> (non visibles par les
            acquéreurs).
          </p>
        </div>
      </div>

      @if (hasContactInPrecisions()) {
        <aura-alert
          [alertInput]="{
            type: auraColorType.WARN,
            title: 'Coordonnées personnelles détectées',
            message:
              'Conformément aux CGU, il est strictement interdit d’inclure des coordonnées directes (téléphone, email, réseaux sociaux...). Ces données seront automatiquement masquées.',
          }"
        ></aura-alert>
      }

      <mat-form-field appearance="outline" class="w-full">
        <textarea
          matInput
          rows="3"
          [formField]="formTree.precisions"
          placeholder="Détaillez ici les éventuelles particularités financières (copropriété, exonérations, etc.)"
          aria-label="Précisions financières complémentaires"
        ></textarea>
        @if (hasContactInPrecisions()) {
          <mat-hint class="text-amber-11 font-medium"
            >⚠️ Coordonnées détectées : elles seront masquées.</mat-hint
          >
        }
      </mat-form-field>
    </div>
  </div>
</div>
